import { gql, useQuery } from "@apollo/client";
import confetti from "canvas-confetti";
import { toast } from "react-toastify";

import { ToastWithDonorDetailBtn } from "@/app/(inbox)/components/Composer/Email/ToastWithDonorDetailBtn";

const GET_USER_COMPLETED_RECS = gql`
  query GetUserCompletedRecs {
    userCurrent {
      id
      completedRecsCount
    }
  }
`;

const IS_TEST_MODE = false;
const MILESTONES = [1, 5, 10, 25, 50, 100, 250, 500, 1000];

const celebrateWithConfetti = (count: number) => {
  // Add a more prominent dimming overlay
  const overlay = document.createElement("div");
  overlay.style.position = "fixed";
  overlay.style.top = "0";
  overlay.style.left = "0";
  overlay.style.width = "100%";
  overlay.style.height = "100%";
  overlay.style.backgroundColor = "rgba(0, 0, 0, 0.7)"; // Darker overlay
  overlay.style.zIndex = "9998";
  overlay.style.transition = "opacity 0.5s ease";
  overlay.style.opacity = "0";
  overlay.style.cursor = "not-allowed"; // Indicate no interaction

  // Add celebratory text with the actual count
  const celebrationText = document.createElement("div");
  celebrationText.style.position = "fixed";
  celebrationText.style.top = "50%";
  celebrationText.style.left = "50%";
  celebrationText.style.transform = "translate(-50%, -50%)";
  celebrationText.style.color = "white";
  celebrationText.style.fontSize = "48px";
  celebrationText.style.fontWeight = "bold";
  celebrationText.style.textAlign = "center";
  celebrationText.style.zIndex = "9999";
  celebrationText.style.animation = "pulse 1s infinite";
  celebrationText.innerHTML = `${count}${count === 1 ? "st" : "th"} Recommendation Complete<br><p style="font-size: 20px;">Take a moment to celebrate</p>`;

  // Add pulse animation
  const style = document.createElement("style");
  style.textContent = `
    @keyframes pulse {
      0% { transform: translate(-50%, -50%) scale(1); }
      50% { transform: translate(-50%, -50%) scale(1.1); }
      100% { transform: translate(-50%, -50%) scale(1); }
    }
  `;
  document.head.appendChild(style);
  document.body.appendChild(overlay);
  document.body.appendChild(celebrationText);

  const defaults = {
    spread: 360,
    ticks: 200,
    gravity: 0.5,
    decay: 0.96,
    startVelocity: 35,
    particleCount: 200,
    scalar: 1.2,
    shapes: ["circle", "square", "star"],
    colors: ["#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF", "#FFFFFF"],
    zIndex: 9999,
    origin: { x: 0.5, y: 0.5 },
  };

  function fire(particleRatio: number, opts: any) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(defaults.particleCount * particleRatio),
      origin: {
        x: 0.5 + (Math.random() * 0.3 - 0.15),
        y: 0.5 + (Math.random() * 0.3 - 0.15),
      },
    });
  }

  // Prevent scrolling and interaction during celebration
  document.body.style.overflow = "hidden";
  overlay.style.opacity = "1";

  // Initial mega burst
  fire(1.5, {
    spread: 100,
    startVelocity: 55,
    scalar: 1.4,
  });

  // Continuous bursts
  let burstCount = 0;
  const maxBursts = 8;
  const burstInterval = setInterval(() => {
    if (burstCount >= maxBursts) {
      clearInterval(burstInterval);
      return;
    }

    fire(0.5, {
      spread: 120,
      startVelocity: 45 + Math.random() * 10,
      decay: 0.92,
      scalar: 1.2 + Math.random() * 0.3,
      origin: {
        x: Math.random() * 0.3 + 0.7, // Randomize origin but keep to right side
        y: Math.random() * 0.3 + 0.7, // Randomize origin but keep to bottom
      },
    });

    burstCount++;
  }, 300);

  // Clean up after celebration
  setTimeout(() => {
    overlay.style.opacity = "0";
    celebrationText.style.opacity = "0";
    document.body.style.overflow = "";

    setTimeout(() => {
      document.body.removeChild(overlay);
      document.body.removeChild(celebrationText);
      document.head.removeChild(style);
    }, 500);
  }, 3000); // Longer celebration duration
};

export function useEmailMilestone() {
  const { data, loading, error } = useQuery(GET_USER_COMPLETED_RECS, {
    fetchPolicy: "network-only",
  });

  const showEmailSentToast = ({ donorId, donorName }: { donorId: string; donorName: string }) => {
    // If query failed, show regular toast
    if (loading || error || !data?.userCurrent) {
      toast.success(
        ToastWithDonorDetailBtn({
          donorId,
          message: `Sent email to ${donorName}`,
        }),
      );
      return;
    }

    const count = data.userCurrent.completedRecsCount + 1;
    const shouldCelebrate = IS_TEST_MODE || MILESTONES.includes(count);

    if (shouldCelebrate) {
      celebrateWithConfetti(count);
    } else {
      toast.success(
        ToastWithDonorDetailBtn({
          donorId,
          message: `Sent email to ${donorName}`,
        }),
      );
    }
  };

  return { showEmailSentToast };
}
